import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMOJIS = [
  { key: 'heart', display: '\u2764\uFE0F', label: 'Adore' },
  { key: 'fire', display: '\uD83D\uDD25', label: 'Feu' },
  { key: 'clap', display: '\uD83D\uDC4F', label: 'Bravo' },
  { key: 'laugh', display: '\uD83D\uDE02', label: 'Haha' },
  { key: 'wow', display: '\uD83D\uDE2E', label: 'Wow' },
  { key: 'idea', display: '\uD83D\uDCA1', label: 'Idee' },
];

const EMOJI_MAP = {};
EMOJIS.forEach(e => { EMOJI_MAP[e.key] = e.display; });

export const ReactionButton = ({ postId, reactions = {}, userReaction, onReactionChange, size = 'md' }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [localReactions, setLocalReactions] = useState(reactions);
  const [localUserReaction, setLocalUserReaction] = useState(userReaction);
  const [animating, setAnimating] = useState(null);
  const pickerRef = useRef(null);
  const btnRef = useRef(null);
  const hoverTimeout = useRef(null);

  useEffect(() => {
    setLocalReactions(reactions);
    setLocalUserReaction(userReaction);
  }, [reactions, userReaction]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReact = async (emoji) => {
    setAnimating(emoji);
    setTimeout(() => setAnimating(null), 400);

    const wasSelected = localUserReaction === emoji;

    // Optimistic update
    const newReactions = { ...localReactions };
    if (wasSelected) {
      newReactions[emoji] = Math.max(0, (newReactions[emoji] || 1) - 1);
      if (newReactions[emoji] === 0) delete newReactions[emoji];
      setLocalUserReaction(null);
    } else {
      if (localUserReaction) {
        newReactions[localUserReaction] = Math.max(0, (newReactions[localUserReaction] || 1) - 1);
        if (newReactions[localUserReaction] === 0) delete newReactions[localUserReaction];
      }
      newReactions[emoji] = (newReactions[emoji] || 0) + 1;
      setLocalUserReaction(emoji);
    }
    setLocalReactions(newReactions);
    setShowPicker(false);

    try {
      await axios.post(`${API}/posts/${postId}/react`, { emoji }, { withCredentials: true });
      if (onReactionChange) onReactionChange();
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);
  const topEmojis = Object.entries(localReactions)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const isSmall = size === 'sm';

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={btnRef}
        onMouseEnter={() => { clearTimeout(hoverTimeout.current); hoverTimeout.current = setTimeout(() => setShowPicker(true), 300); }}
        onMouseLeave={() => { clearTimeout(hoverTimeout.current); hoverTimeout.current = setTimeout(() => setShowPicker(false), 500); }}
        onClick={() => {
          if (localUserReaction) {
            handleReact(localUserReaction);
          } else {
            handleReact('heart');
          }
        }}
        className={`flex items-center gap-1.5 rounded-lg transition-all ${
          localUserReaction
            ? 'text-pink-500 bg-pink-50 hover:bg-pink-100'
            : 'text-gray-500 hover:text-pink-500 hover:bg-pink-50'
        } ${isSmall ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}`}
        data-testid={`reaction-btn-${postId}`}
      >
        {topEmojis.length > 0 ? (
          <span className="flex items-center -space-x-0.5">
            {topEmojis.map(([key]) => (
              <span key={key} className={`${isSmall ? 'text-sm' : 'text-base'} ${animating === key ? 'animate-popBounce' : ''}`}>
                {EMOJI_MAP[key]}
              </span>
            ))}
          </span>
        ) : (
          /* Brief LOT6.2 — état vide = cœur en CONTOUR (icône), plus d'emoji
             rouge rempli qui donnait l'impression d'un like déjà posé. */
          <Heart
            className={`${isSmall ? 'h-4 w-4' : 'h-5 w-5'} ${localUserReaction === 'heart' ? 'fill-current animate-popBounce' : ''}`}
            data-testid={`reaction-heart-outline-${postId}`}
          />
        )}
        <span className="font-medium">{totalReactions || ''}</span>
      </button>

      {/* Emoji Picker Popover */}
      {showPicker && (
        <div
          ref={pickerRef}
          onMouseEnter={() => clearTimeout(hoverTimeout.current)}
          onMouseLeave={() => { hoverTimeout.current = setTimeout(() => setShowPicker(false), 300); }}
          className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-full shadow-xl border border-gray-100 dark:border-gray-700 px-2 py-1.5 flex items-center gap-0.5 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200"
          data-testid={`reaction-picker-${postId}`}
        >
          {EMOJIS.map((emoji) => (
            <button
              key={emoji.key}
              onClick={() => handleReact(emoji.key)}
              className={`group relative p-1.5 rounded-full transition-all duration-200 hover:scale-125 hover:bg-gray-100 ${
                localUserReaction === emoji.key ? 'bg-purple-100 scale-110' : ''
              }`}
              title={emoji.label}
              data-testid={`emoji-${emoji.key}-${postId}`}
            >
              <span className="text-xl leading-none">{emoji.display}</span>
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {emoji.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReactionSummary = ({ reactions = {}, size = 'sm' }) => {
  if (!reactions || Object.keys(reactions).length === 0) return null;
  const sorted = Object.entries(reactions).sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((sum, [, count]) => sum + count, 0);
  
  return (
    <div className={`flex items-center gap-1 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
      <span className="flex -space-x-1">
        {sorted.slice(0, 3).map(([key]) => (
          <span key={key} className={size === 'sm' ? 'text-sm' : 'text-base'}>{EMOJI_MAP[key]}</span>
        ))}
      </span>
      <span className="text-gray-500 ml-0.5">{total}</span>
    </div>
  );
};

export { EMOJIS, EMOJI_MAP };
export default ReactionButton;
